<template>
  <div v-if="value">
    <v-card flat>
      <v-card-text>
        <v-row>
          <ConfigSlider
            v-model="blurWidth"
            icon="mdi-arrow-expand-horizontal"
            label="Blur Width"
            :disabled="!pointsReady"
          />
          <ConfigSlider
            v-model="blurHeight"
            icon="mdi-arrow-expand-vertical"
            label="Blur Height"
            :disabled="!pointsReady"
          />
          <ConfigSlider
            v-model="markerSize"
            icon="mdi-circle-expand"
            label="Marker Size"
            :disabled="!pointsReady"
          />
          <ConfigSlider
            v-model="midlineWidth"
            icon="mdi-arrow-split-vertical"
            label="Midline Width"
            :disabled="!pointsReady"
          />
        </v-row>
        <v-row>
          <v-col cols="8" class="py-0">
            <ActionButton
              color="error"
              icon="mdi-close"
              label="Clear Points"
              :disabled="!pointsReady || loading"
              :loading="loading"
              @action="resetPoints"
            />
            <ActionButton
              color="success"
              icon="mdi-arrow-split-vertical"
              label="Blur With Midline"
              :disabled="!pointsReady || loading"
              :loading="loading"
              @action="applyBlurEffect(midlineWidth.base)"
            />
            <ActionButton
              color="success"
              icon="mdi-blur-linear"
              label="Blur Only"
              :disabled="!pointsReady || loading"
              :loading="loading"
              @action="applyBlurEffect"
            />
          </v-col>
          <v-col cols="4" class="py-0 text-right">
            <ActionButton
              v-if="isAdmin"
              color="warning"
              icon="mdi-restore"
              label="Restore Image"
              :disabled="loading"
              :loading="loading"
              @action="restoreImage"
            />
            <ActionButton
              color="primary"
              icon="mdi-content-save"
              label="Save"
              :disabled="!pointsReady || loading"
              :loading="loading"
              @action="saveFile"
            />
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card flat>
      <v-card-text class="pt-0">
        <div
          ref="canvasContainer"
          @click="handleCanvasClick"
          @mousedown="handleMouseDown"
          @mousemove="handleMouseMove"
          @mouseup="handleMouseUp"
          @mouseleave="handleMouseLeave"
        >
          <canvas
            ref="canvasRef"
            :style="{
              cursor: pointsReady ? currentCursor : 'crosshair'
            }"
          />
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { CanvasManager } from "./canvas_manager";
import { createParamConfig, loadImage, canvasToBlob } from "./helpers";
import ActionButton from "./ActionButton";
import ConfigSlider from "./ConfigSlider";

export default {
  name: "ImageEditor",

  components: { ActionButton, ConfigSlider },

  props: ["value"],

  data() {
    return {
      canvasManager: null,
      blurWidth: createParamConfig(0),
      blurHeight: createParamConfig(0),
      markerSize: createParamConfig(0),
      midlineWidth: createParamConfig(0),
      points: [],
      loading: false,
      isDragging: false,
      draggingIndex: -1,
      currentCursor: "default"
    };
  },

  watch: {
    value: {
      immediate: true,
      async handler(file) {
        if (!file) return;
        await this.initialize();
      }
    },

    blurWidth: {
      handler() {
        this.applyBlurEffect();
      },
      deep: true
    },

    blurHeight: {
      handler() {
        this.applyBlurEffect();
      },
      deep: true
    },

    midlineWidth: {
      handler() {
        this.applyBlurEffect(this.midlineWidth.base);
      },
      deep: true
    },

    markerSize: {
      handler() {
        this.canvasManager.clear();
        this.drawMarkers();
      },
      deep: true
    }
  },
  computed: {
    ...mapGetters(["me"]),

    imageUrl: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      }
    },

    pointsReady() {
      return this.points.length === 2;
    },

    isAdmin() {
      return this.me?.admin || this.me?.all_departments.includes("DESIGN");
    }
  },

  methods: {
    async initialize() {
      try {
        this.loading = true;
        const image = await loadImage(this.imageUrl);
        this.canvasManager = new CanvasManager(this.$refs.canvasRef, image);
        this.initializeParameters(image);
      } catch (error) {
        console.error("Initialization error:", error);
        this.$emit("error", error);
      } finally {
        this.loading = false;
      }
    },

    initializeParameters(image) {
      const BASE_RATE = 0.1;
      this.blurWidth = createParamConfig(image.width * BASE_RATE);
      this.blurHeight = createParamConfig(image.height * BASE_RATE);
      this.markerSize = createParamConfig(image.height * BASE_RATE * 0.1);
      this.midlineWidth = createParamConfig(image.width * BASE_RATE * 0.1);
    },

    handleCanvasClick(event) {
      if (this.pointsReady || this.isDragging) return;

      const point = this.canvasManager.getScaledCoordinates(event);
      this.points.push(point);
      this.drawMarkers();
    },

    handleMouseDown(event) {
      if (!this.pointsReady) return;

      const point = this.canvasManager.getScaledCoordinates(event);
      this.draggingIndex = this.points.findIndex((p) => {
        return (
          Math.hypot(p.x - point.x, p.y - point.y) < this.markerSize.base * 2
        );
      });

      if (this.draggingIndex > -1) {
        this.isDragging = true;
        this.currentCursor = "grabbing";
      }
    },

    handleMouseMove(event) {
      if (!this.isDragging) {
        this.updateCursor(event);
        return;
      }

      const point = this.canvasManager.getScaledCoordinates(event);
      this.points.splice(this.draggingIndex, 1, point);
      this.canvasManager.clear();
      this.drawMarkers();
    },

    handleMouseUp() {
      if (this.isDragging) {
        this.isDragging = false;
        this.draggingIndex = -1;
        this.currentCursor = "pointer";
      }
    },

    handleMouseLeave() {
      if (this.isDragging) {
        this.isDragging = false;
        this.draggingIndex = -1;
        this.currentCursor = "default";
      }
    },

    updateCursor(event) {
      if (!this.pointsReady) {
        this.currentCursor = "crosshair";
        return;
      }

      const point = this.canvasManager.getScaledCoordinates(event);
      const isHovering = this.points.some(
        (p) =>
          Math.hypot(p.x - point.x, p.y - point.y) < this.markerSize.base * 2
      );
      this.currentCursor = isHovering ? "pointer" : "default";
    },

    drawMarkers() {
      this.canvasManager.drawMarkers(this.points, this.markerSize.base);
    },

    async applyBlurEffect(midline) {
      if (!this.pointsReady) return;

      this.canvasManager.clear();
      this.canvasManager.applyBlurEffect(
        this.points,
        this.blurWidth.base,
        this.blurHeight.base,
        this.markerSize.base,
        midline
      );
    },

    restoreImage() {
      this.loading = true;
      this.$emit("restore");
    },

    resetPoints() {
      this.points = [];
      this.canvasManager.clear();
    },

    async saveFile() {
      this.loading = true;
      const blob = await canvasToBlob(this.canvasManager.canvas);
      this.$emit("update", blob);
    }
  }
};
</script>
